import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { PASS_THROUGH_401, OWNER_AUTH_REQUEST } from '../services/session-policy';

/**
 * SESSION TIMEOUT INTERCEPTOR
 * Handles:
 * 1. 401 {code:'SESSION_EXPIRED'} (the server's 15-min idle rule) — signs out once through AuthService.endSession,
 *    with no renewal (the server would refuse it anyway). 401s from the public auth endpoints pass through.
 * 2. Shows a non-blocking toast notification on timeout
 *
 * Decide by code, never by message: TOKEN_REVOKED ("Session has been ended…") is also sent for the access token a
 * renewal just rotated, and must reach AuthInterceptor's wait-for-renewal path; "…session terminated…" (reuse) must
 * reach it too.
 *
 * NOTE: the sliding-window session reset is NOT done here. It is driven by DOM-activity
 * listeners in AuthService — HTTP traffic deliberately does NOT extend an unattended session
 * (so background polling can't keep a shared workstation logged in).
 */
@Injectable()
export class SessionTimeoutInterceptor implements HttpInterceptor {
  private hasShownExpiry = false; // Prevent multiple toasts for parallel 401s

  constructor(private authService: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // The support-session renewal sent with the owner's own token: AuthService reads the refusal itself (owner's
        // sign-in ended → /login with the reason). Ending the session here would run the support-tab path instead and
        // send the owner, with a dead sign-in, back to the owner console.
        if (request.context.get(OWNER_AUTH_REQUEST)) {
          return throwError(() => error);
        }

        // A support session whose approval ended is NOT a sign-in timeout: AuthInterceptor returns the
        // owner to their own account (logging out here would also drop the owner's kept-aside sign-in).
        if (error.status === 401 && error.error?.code === 'SUPPORT_SESSION_ENDED') {
          return throwError(() => error);
        }

        if (error.status === 401 && error.error?.code === 'SESSION_EXPIRED' && !PASS_THROUGH_401.test(request.url)) {
          if (this.authService.isLoggedIn) {
            // Show non-blocking toast instead of alert() — once for parallel 401s
            if (!this.hasShownExpiry) {
              this.hasShownExpiry = true;
              this.showSessionExpiredToast();
              setTimeout(() => { this.hasShownExpiry = false; }, 3000);
            }
            this.authService.endSession({ kind: 'forced', code: 'SESSION_EXPIRED', message: error.error?.message });
          }
          // LOW-4 (audit 2026-06-16): this interceptor OWNS the session-expired logout. SWALLOW the
          // error here (return EMPTY) instead of re-throwing, so AuthInterceptor never handles it a second time.
          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  /** Non-blocking toast notification instead of alert() */
  private showSessionExpiredToast(): void {
    const toast = document.createElement('div');
    toast.textContent = 'Session expired. Please login again.';
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#dc2626',
      color: '#fff',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: '99999',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transition: 'opacity 0.3s ease'
    });
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}
